
import api from "./api";
import mockApi from "./mockApi";
import { API_CONFIG } from "../config/apiConfig";
import {
  ActivateUserRequest,
  ApiResponse,
  AuthResponse,
  BasicResponse,
  CreateUserRequest,
  CreateUserResponse,
  LoginCredentials,
  LoginResponse,
  SendOtpRequest,
  User,
  VerifyOTPRequest,
  GetAllUsersQueryParams,
  AllUsersResponse,
  UserDetails,
} from "../types/api";
import { AxiosInstance } from "axios";
import { hashPassword } from "../utils/passwordhash";

// Use mock API in development or when specified
const USE_MOCK_API =
  process.env.REACT_APP_USE_MOCK === "true" && API_CONFIG.IS_DEVELOPMENT;
// const apiClient = USE_MOCK_API ? (mockApi as AxiosInstance) : (api as AxiosInstance);
const apiClient = USE_MOCK_API ? (mockApi as any) : (api as AxiosInstance); // TODO fix mockApi type

const authService = {
  // Login user (password-based or hybrid)
  login: async (credentials: LoginCredentials): Promise<BasicResponse<LoginResponse>> => {
    try {
      const payload = {
        mobileNumber: credentials.mobileNumber,
        password: hashPassword(
          credentials.password ?? "",
          credentials.mobileNumber || ""
        ),
        otp: credentials.otp ?? "",
      };
      const response: ApiResponse<BasicResponse<LoginResponse>> = await apiClient.post(
        "/api/User/login",
        payload
      );
      return response.data;
    } catch (error: any) {
      console.error("Login error:", error);

      if (error.code === "ECONNREFUSED" || error.code === "ERR_NETWORK") {
        throw new Error(
          "Unable to connect to server. Please check if the backend is running."
        );
      }

      if (error.code === "ECONNABORTED") {
        throw new Error("Request timeout. Please try again.");
      }

      if (error.response) {
        const { status, data } = error.response;
        if (status === 401) {
          // Normalize message to avoid page reloads; UI will show inline error
          throw new Error(
            data?.message || "Invalid credentials. Please check your credentials."
          );
        } else if (status === 404) {
          throw new Error("User not found. Please check your credentials.");
        } else if (status >= 500) {
          throw new Error("Server error. Please try again later.");
        }
        throw new Error(data.message || "Login failed");
      }

      throw new Error("Login failed. Please try again.");
    }
  },

  // Register user
  register: async (
    userData: CreateUserRequest
  ): Promise<BasicResponse<CreateUserResponse>> => {
    try {
      const response: ApiResponse<BasicResponse<CreateUserResponse>> = await apiClient.post(
        "/api/User/createUser",
        userData
      );
      console.log("Register resp", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Registration error:", error);

      if (error.code === "ECONNREFUSED" || error.code === "ERR_NETWORK") {
        throw new Error(
          "Unable to connect to server. Please check if the backend is running."
        );
      }

      if (error.code === "ECONNABORTED") {
        throw new Error("Request timeout. Please try again.");
      }

      if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          throw new Error(data.message || "Invalid registration data");
        } else if (status === 409) {
          throw new Error(
            "Phone number already registered"
          );
        } else if (status >= 500) {
          throw new Error("Server error. Please try again later.");
        }
        throw new Error(data.message || "Registration failed");
      }

      throw new Error("Registration failed. Please try again.");
    }
  },

  // Update current userDetails if token is still valid
  getMe: async () : Promise<User> => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user")|| "{}");
      if(storedUser.userId){
        const response: ApiResponse<BasicResponse<User>> = await apiClient.get(`/api/User/userDetails?userId=${storedUser.userId}`);
        return response.data.data;
      }
      throw Error("No UserId found in store")
    } catch (error: any) {
      try {
        if (error.response) {
        const { status, data } = error.response;
        if (status === 401) {
          // Normalize message to avoid page reloads; UI will show inline error
          throw new Error(
            "Login Expired, Please login again."
          );
        } else if (status === 404) {
          throw new Error("User not found. Please check your credentials.");
        } else if (status >= 500) {
          throw new Error("Server error. Please try again later.");
        }
      }
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        return JSON.parse(storedUser);
      }
      throw "Failed to fetch User Details from store"
      } catch (_) {}
      throw error.response?.data || error.message;
    }
  },

  // Verify OTP
  verifyOTP: async (otpData: VerifyOTPRequest): Promise<BasicResponse<boolean>> => {
    try {
      const response: ApiResponse<BasicResponse<boolean>> = await apiClient.post("/api/User/verifyOTP", otpData);
      return response.data;
    } catch (error: any) {
      const message =
        (error &&
          error.response &&
          (error.response.data?.message ||
            error.response.data?.error ||
            error.response.data)) ||
        error.message ||
        "Failed to verify OTP";
      throw new Error(
        typeof message === "string" ? message : "Failed to verify OTP"
      );
    }
  },

  // Send OTP
  sendOTP: async (otpRequest: SendOtpRequest): Promise<BasicResponse<string>> => {
    try {
      const response: ApiResponse<BasicResponse<string>> = await apiClient.post("/api/User/sendOTP", otpRequest);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },
  resendOtp: async (otpRequest: SendOtpRequest) => {
    try {
      const response = await apiClient.post("/api/User/sendOTP", otpRequest);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Setup password
  setupPassword: async (passwordData: ActivateUserRequest): Promise<BasicResponse<CreateUserResponse>> => {
    try {
      const response: ApiResponse<BasicResponse<CreateUserResponse>> = await apiClient.post(
        "/api/User/activateUser",
        passwordData
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Forgot password
  forgotPassword: async (mobileNumber: string): Promise<BasicResponse<string>> => {
    try {
      const response: ApiResponse<BasicResponse<string>> = await apiClient.post("/api/User/sendOTP", {
        mobileNumber,
        type: "Mobile",
        requestType: "ResetPassword",
      } as SendOtpRequest);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },


  // Logout (client-side only)
  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },

  getAllUsers: async (params: GetAllUsersQueryParams): Promise<BasicResponse<AllUsersResponse>> => {
    try {
      const response: ApiResponse<BasicResponse<AllUsersResponse>> = await apiClient.get("/api/User/allUsers", { params });
      return response.data;
    } catch (error: any) {
      console.error("Get all users error:", error);
      throw error.response?.data || error.message;
    }
  },

  getUserDetails: async (userId: string): Promise<BasicResponse<UserDetails>> => {
    try {
      const response: ApiResponse<BasicResponse<UserDetails>> = await apiClient.get("/api/User/userDetails", { params: { userId } });
      return response.data;
    } catch (error: any) {
      console.error("Get user details error:", error);
      throw error.response?.data || error.message;
    }
  },
};

export default authService;
